import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AssistantAttachment,
  AssistantNavAction,
  AssistantProductCard,
  AssistantRequestCard,
} from '../../features/assistant/interfaces/assistant.interface';

const BRAND = '#E55325';

interface Props {
  attachment: AssistantAttachment;
  onNavigate: () => void;
}

const ProductCards: React.FC<{ items: AssistantProductCard[]; onNavigate: () => void }> = ({
  items,
  onNavigate,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mt-2 flex flex-col gap-2">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => {
            navigate(item.path);
            onNavigate();
          }}
          className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-2 text-left shadow-sm transition-colors hover:border-[#E55325]/50 hover:bg-[#E55325]/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
        >
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100">
            {item.image ? (
              <img src={item.image} alt="" className="h-full w-full object-cover" loading="lazy" />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#212121]">
              {item.title}
              {item.isVerifiedSeller ? (
                <span
                  className="ml-1 align-middle text-[10px] font-bold uppercase tracking-wide"
                  style={{ color: '#22c55e' }}
                  title="Verified seller"
                >
                  ✓
                </span>
              ) : null}
            </p>
            <p className="truncate text-xs text-[#6A6A6A]">
              <span className="font-semibold" style={{ color: '#E55325' }}>
                {item.price}
              </span>{' '}
              · {item.origin}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

const RequestCards: React.FC<{ items: AssistantRequestCard[]; onNavigate: () => void }> = ({
  items,
  onNavigate,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mt-2 flex flex-col gap-2">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => {
            navigate(item.path);
            onNavigate();
          }}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:border-[#E55325]/50 hover:bg-[#E55325]/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
        >
          <p className="truncate text-sm font-semibold text-[#212121]">{item.productName}</p>
          <p className="truncate text-xs text-[#6A6A6A]">
            {[item.origin, item.budget].filter(Boolean).join(' · ')}
          </p>
        </button>
      ))}
    </div>
  );
};

const NavActions: React.FC<{ actions: AssistantNavAction[]; onNavigate: () => void }> = ({
  actions,
  onNavigate,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {actions.map((action) => (
        <button
          key={action.path}
          type="button"
          onClick={() => {
            navigate(action.path);
            onNavigate();
          }}
          className="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
          style={{ borderColor: BRAND, color: BRAND }}
        >
          {action.label}
          <span aria-hidden className="ml-1">→</span>
        </button>
      ))}
    </div>
  );
};

const AssistantAttachments: React.FC<Props> = ({ attachment, onNavigate }) => {
  switch (attachment.type) {
    case 'products':
      return <ProductCards items={attachment.items} onNavigate={onNavigate} />;
    case 'requests':
      return <RequestCards items={attachment.items} onNavigate={onNavigate} />;
    case 'navigation':
      return <NavActions actions={attachment.actions} onNavigate={onNavigate} />;
    case 'escalated':
      return (
        <div className="mt-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
          Sent to our support team. Your reference is{' '}
          <span className="font-semibold">{attachment.reference}</span>.
        </div>
      );
    default:
      return null;
  }
};

/** Memoised: attachments never change once a turn has finished streaming. */
export default React.memo(AssistantAttachments);
